import { useCallback, useEffect, useRef, useState } from "react"

import {
  DefaultBrowserBehavior,
  DefaultModality,
  VoiceFocusDeviceTransformer,
} from "amazon-chime-sdk-js"

import { ErrorType } from "../Cloudroom"
import {
  createChimeAudioVideo,
  createVoiceFocusTransformer,
  setUpVideoElement,
  startAudio,
  startVideo,
} from "../cloudroomHelpers/chimeAudioVideoHelpers"

import { CloudroomRosterHandlers } from "./useRoster"

export type UseChimeCloudroomProps = {
  session: any
  localUser: {
    type: "Student" | "Tutor" | "Teacher" | "Admin"
    id: string
  }
  rosterHandlers: CloudroomRosterHandlers
  ghost?: boolean
  muteOutput?: boolean
  error?: ErrorType
  handleError?: (error: ErrorType) => void
}

const useChimeCloudroom = ({
  session,
  localUser,
  rosterHandlers,
  ghost = false,
  muteOutput = false,
  handleError,
}: UseChimeCloudroomProps) => {
  const {
    chimeJoinInfo: { meeting, attendee },
    lowBandwidthCloudroom,
    audioMutedUserIds,
    videoMutedUserIds,
    id: sessionId,
  } = session
  const lowBandwidthStudent =
    lowBandwidthCloudroom && localUser.type === "Student"

  const {
    updateRosterOnAttendeePresenceEvent,
    updateRosterOnUpdatedVideoTile,
    updateRosterOnRemovedVideoTile,
    clearRoster,
  } = rosterHandlers

  const [audioSetupInProgress, setAudioSetupInProgress] = useState(true)
  const [videoSetupInProgress, setVideoSetupInProgress] = useState(true)
  const [audioVideo, setAudioVideo] = useState<any>()
  const voiceFocusTransformerRef = useRef<VoiceFocusDeviceTransformer | null>(
    null
  )

  const configureMicrophone = useCallback(async () => {
    if (!audioVideo) return
    await startAudio(audioVideo, handleError, voiceFocusTransformerRef.current)
  }, [audioVideo, handleError])

  useEffect(() => {
    let cancelled = false
    let disconnect: (() => void) | undefined

    const handlePageHide = (event: PageTransitionEvent) => {
      if (!event.persisted) disconnect?.()
    }

    const audioVideoSetup = async () => {
      const transformer = ghost
        ? null
        : await createVoiceFocusTransformer(sessionId)

      const enableWebAudio = transformer != null
      const audioVideo = createChimeAudioVideo(
        enableWebAudio,
        meeting,
        attendee
      )

      if (cancelled) {
        audioVideo.stop()
        if (transformer) {
          void VoiceFocusDeviceTransformer.destroyVoiceFocus(transformer)
        }
        return
      }

      voiceFocusTransformerRef.current = transformer
      setAudioVideo(audioVideo)

      // SET OBSERVER
      const observer = {
        audioVideoDidStart: async () => {
          // set up audio output
          if (!muteOutput) {
            const browserBehavior = new DefaultBrowserBehavior()
            if (browserBehavior.supportsSetSinkId()) {
              audioVideo
                .listAudioOutputDevices()
                .then(devices => {
                  const audioOutputDeviceInfo = devices[0]
                  audioVideo.chooseAudioOutput(audioOutputDeviceInfo?.deviceId)
                })
                .catch(handleError)
            }

            const audioElement: HTMLAudioElement =
              document.querySelector("#room-audio")
            audioVideo.bindAudioElement(audioElement)
            setTimeout(() => audioElement.play(), 500)
          }

          // set video input initial state
          const videoIsOn =
            !lowBandwidthStudent &&
            !ghost &&
            !videoMutedUserIds.includes(localUser.id)

          if (videoIsOn) startVideo(audioVideo, handleError)

          setVideoSetupInProgress(false)

          // set audio input initial state
          if (!ghost) {
            await startAudio(audioVideo, handleError, transformer)

            audioVideo.realtimeSetCanUnmuteLocalAudio(true)
            audioVideo.realtimeUnmuteLocalAudio()

            const initialAudioMuted = audioMutedUserIds.includes(localUser.id)

            if (initialAudioMuted) {
              audioVideo.stopAudioInput()
              audioVideo.realtimeMuteLocalAudio()
            }
          }
          setAudioSetupInProgress(false)
        },
        videoTileDidUpdate: tileState => {
          if (!tileState.boundAttendeeId) return

          const baseAttendeeId = new DefaultModality(
            tileState.boundAttendeeId
          ).base()

          updateRosterOnUpdatedVideoTile(tileState)

          if (tileState.isContent) {
            const localAttendeeId = attendee.attendeeId

            setUpVideoElement({
              audioVideo: audioVideo,
              local: baseAttendeeId === localAttendeeId,
              attendeeId: baseAttendeeId,
              tileId: tileState.tileId,
            })
            return
          }

          if (tileState.localTile && (ghost || lowBandwidthStudent)) return

          setUpVideoElement({
            audioVideo: audioVideo,
            local: tileState.localTile,
            attendeeId: tileState.boundAttendeeId,
            tileId: tileState.tileId,
          })
        },
        videoTileWasRemoved: updateRosterOnRemovedVideoTile,
      }
      audioVideo.addObserver(observer)
      audioVideo.start()
      audioVideo.realtimeSubscribeToAttendeeIdPresence(
        updateRosterOnAttendeePresenceEvent
      )

      disconnect = () => {
        audioVideo.realtimeUnsubscribeToAttendeeIdPresence(
          updateRosterOnAttendeePresenceEvent
        )
        audioVideo.stopLocalVideoTile()
        audioVideo.stopVideoInput()
        audioVideo.stopAudioInput()
        if (transformer) {
          void VoiceFocusDeviceTransformer.destroyVoiceFocus(transformer)
          voiceFocusTransformerRef.current = null
        }
        audioVideo.stop()
        audioVideo.removeObserver(observer)
        clearRoster()
      }

      window.addEventListener("pagehide", handlePageHide)
    }

    audioVideoSetup()

    return () => {
      cancelled = true
      window.removeEventListener("pagehide", handlePageHide)
      disconnect?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    audioVideo,
    configureMicrophone,
    audioSetupInProgress,
    videoSetupInProgress,
    voiceFocusTransformerRef,
  }
}
export default useChimeCloudroom
